import type { ReactElement, ReactNode } from 'react';

import { cn } from './cn';

interface StatePanelProps {
  /** Ícono decorativo (lucide), se oculta a lectores de pantalla. */
  icon: ReactNode;
  title: string;
  children: ReactNode;
  /** `danger` para fallas (ícono rojo); `muted` para estados informativos. */
  tone?: 'muted' | 'danger';
  role?: 'alert' | 'status';
  /** Con reintento: botón "Reintentar" a la derecha (errores recuperables). */
  onRetry?: () => void;
  /** Reintento en curso: el botón lo indica y no admite otro clic. */
  retrying?: boolean;
  /** Clases extra de animación (`d1`, `d2`...). */
  className?: string;
}

/**
 * Tarjeta de estado de una pantalla: sin permiso, sin empresa activa, no encontrado o
 * error recuperable (UX-ERR-01: qué pasó y qué hacer). Una sola pieza para todas las
 * pantallas, así el lenguaje visual de los estados no diverge.
 */
export function StatePanel({
  icon,
  title,
  children,
  tone = 'muted',
  role,
  onRetry,
  retrying = false,
  className,
}: StatePanelProps): ReactElement {
  return (
    <div className={cn('card rise', className)} role={role} style={{ padding: onRetry ? 24 : 32 }}>
      <div className="rowb" style={{ gap: 12 }}>
        <span
          aria-hidden="true"
          style={{
            color: tone === 'danger' ? 'var(--danger)' : 'var(--ink-faint)',
            display: 'flex',
          }}
        >
          {icon}
        </span>
        <div>
          <h2 style={{ fontSize: 15, marginBottom: 4 }}>{title}</h2>
          <p className="soft" style={{ fontSize: 13 }}>
            {children}
          </p>
        </div>
        {onRetry && (
          <button
            type="button"
            className="btn mut sm"
            onClick={onRetry}
            disabled={retrying}
            style={{ marginLeft: 'auto' }}
          >
            {retrying ? 'Reintentando…' : 'Reintentar'}
          </button>
        )}
      </div>
    </div>
  );
}

/** Error recuperable dentro de una tarjeta ya abierta (sin título propio). `dense`: dentro de
 *  un campo o una lista de selección, sin el relleno de cuerpo de tarjeta. */
export function InlineError({
  icon,
  children,
  onRetry,
  retrying = false,
  dense = false,
}: {
  icon: ReactNode;
  children: ReactNode;
  onRetry: () => void;
  /** Reintento en curso: el botón lo indica y no admite otro clic (igual que StatePanel). */
  retrying?: boolean;
  dense?: boolean;
}): ReactElement {
  return (
    <div className={dense ? 'inline-error-dense rowb' : 'cb rowb'} style={{ gap: 12 }} role="alert">
      <span aria-hidden="true" style={{ color: 'var(--danger)', display: 'flex' }}>
        {icon}
      </span>
      <p className="soft" style={{ fontSize: 13, margin: 0 }}>
        {children}
      </p>
      <button
        type="button"
        className="btn mut sm"
        style={{ marginLeft: 'auto' }}
        onClick={onRetry}
        disabled={retrying}
      >
        {retrying ? 'Reintentando…' : 'Reintentar'}
      </button>
    </div>
  );
}
